import { memo } from "react";
import { Box, Typography } from "@mui/material";
import { getPatrolLinkHotspotScreenLayout } from "./patrolHotspotProjection";

/** Floor chevron — matches Marzipano / virtual-tour link marker (no ring). */
const CHEVRON_WIDTH = 88;
const CHEVRON_HEIGHT = 64;

/**
 * Flat white chevron on the “floor”; rotates toward the next pin bearing.
 */
function FloorChevronIcon({ rotateZDeg = 0 }) {
  return (
    <Box
      sx={{
        width: CHEVRON_WIDTH,
        height: CHEVRON_HEIGHT,
        transform: `rotateZ(${rotateZDeg}deg)`,
        transformOrigin: "50% 78%",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          perspective: "320px",
          perspectiveOrigin: "50% 90%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            transform: "rotateX(62deg)",
            transformOrigin: "50% 100%",
          }}
        >
          <svg
            width={CHEVRON_WIDTH}
            height={CHEVRON_HEIGHT}
            viewBox="0 0 88 64"
            aria-hidden
            style={{ display: "block" }}
          >
            <path
              d="M 44 4 L 78 58 L 62 58 L 44 30 L 26 58 L 10 58 Z"
              fill="#FFFFFF"
            />
          </svg>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Link hotspot — fixed floor height; slides and points along pin route direction.
 */
export const PatrolLinkHotspot = memo(function PatrolLinkHotspot({
  hotspotYawDeg,
  viewerYawDeg,
  viewerPitchDeg = 0,
  verticalFovDeg = 75,
  viewerAspect = 16 / 9,
  label = "",
  onClick,
  disabled = false,
}) {
  const layout = getPatrolLinkHotspotScreenLayout({
    hotspotYawDeg,
    viewerYawDeg,
    viewerPitchDeg,
    verticalFovDeg,
    aspect: viewerAspect,
  });

  if (!layout.visible) {
    return null;
  }

  const canClick =
    layout.directionMatched && typeof onClick === "function" && !disabled;

  const handleActivate = (e) => {
    if (!canClick) return;
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  return (
    <Box
      role={canClick ? "button" : undefined}
      tabIndex={canClick ? 0 : -1}
      aria-label={
        canClick && label
          ? `Go to ${label}`
          : canClick
            ? "Go to next location"
            : undefined
      }
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (!canClick) return;
        if (e.key === "Enter" || e.key === " ") {
          handleActivate(e);
        }
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        left: `${layout.xPct}%`,
        top: `${layout.yPct}%`,
        transform: "translate(-50%, -50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: 0,
        padding: 0,
        width: "max-content",
        opacity: disabled ? 0.45 : 1,
        pointerEvents: canClick ? "auto" : "none",
        cursor: canClick ? "pointer" : "default",
        zIndex: 1,
      }}
    >
      <FloorChevronIcon rotateZDeg={layout.chevronRotationDeg} />

      {label ? (
        <Typography
          component="span"
          sx={{
            mt: 0.5,
            px: 0.75,
            py: 0.2,
            borderRadius: "6px",
            border: "2px solid #54C600",
            bgcolor: "#FFFFFF",
            color: "#111827",
            fontSize: "11px",
            fontWeight: 700,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  );
});
